/**
 * Session lifetime (FR-ADMIN-047): an idle timeout that activity pushes back and an absolute timeout that nothing
 * extends. Lengths come from configuration; these functions only do the arithmetic.
 */

export interface SessionLimits {
  idleMs: number;
  absoluteMs: number;
}

export interface SessionTimes {
  createdAt: number;
  lastActiveAt: number;
}

export type SessionClockState = "active" | "idle_timeout" | "absolute_timeout";

/** setTimeout cannot wait longer than 2^31 − 1 ms; longer waits are re-armed. */
export const MAX_TIMER_MS = 2_147_483_647;

export function idleExpiresAt(times: SessionTimes, limits: SessionLimits): number {
  return Math.min(times.lastActiveAt + limits.idleMs, absoluteExpiresAt(times, limits));
}

export function absoluteExpiresAt(times: SessionTimes, limits: SessionLimits): number {
  return times.createdAt + limits.absoluteMs;
}

export function sessionClock(
  times: SessionTimes,
  limits: SessionLimits,
  now: number,
): SessionClockState {
  if (now >= absoluteExpiresAt(times, limits)) return "absolute_timeout";
  if (now >= times.lastActiveAt + limits.idleMs) return "idle_timeout";
  return "active";
}

/** Activity moves the idle end; it never moves the absolute end. */
export function touch(times: SessionTimes, now: number): SessionTimes {
  return { ...times, lastActiveAt: Math.max(times.lastActiveAt, now) };
}

/** Delay before the client should check the session again: at the next expiry or the regular interval. */
export function nextCheckDelay(expiresAt: number, now: number, intervalMs: number): number {
  const untilExpiry = Math.max(0, expiresAt - now);
  return Math.min(untilExpiry, intervalMs, MAX_TIMER_MS);
}
